import type { IconKey } from './iconRegistry'
import { getDisplayText } from './display'

export type StatusTone = 'success' | 'warning' | 'danger' | 'inverse' | 'neutral'

export const STATUS_EMOJI_ICON_MAP: Record<string, { iconKey: IconKey; tone: StatusTone }> = {
  '✅': { iconKey: 'check-circle', tone: 'success' },
  '⚠️': { iconKey: 'alert-triangle', tone: 'warning' },
  '🔴': { iconKey: 'alert-circle', tone: 'danger' },
  '❌': { iconKey: 'x-circle', tone: 'inverse' },
}

const CANCELLED_STATUS_PATTERN = /(ยกเลิก|cancel)/i

/** Receivable / outstanding-order statuses (plain Thai labels, no emoji prefix). */
const RECEIVABLE_STATUS_MAP: Record<
  string,
  { iconKey: IconKey; tone: StatusTone }
> = {
  ค้าง: { iconKey: "alert-circle", tone: "danger" },
  รอตรวจ: { iconKey: "alert-triangle", tone: "neutral" },
  ลบรายการ: { iconKey: "x-circle", tone: "inverse" },
  รายการที่ลบ: { iconKey: "x-circle", tone: "inverse" },
  หมดอายุ: { iconKey: "calendar", tone: "neutral" },
  รับบางส่วน: { iconKey: "help-circle", tone: "warning" },
  เหลือรอจัดสรร: { iconKey: "help-circle", tone: "warning" },
};

const WORK_QUEUE_STATUS_MAP: Record<
  string,
  { iconKey: IconKey; tone: StatusTone }
> = {
  รอการตัดสินใจ: { iconKey: "alert-triangle", tone: "warning" },
  รอจับคู่: { iconKey: "alert-triangle", tone: "warning" },
  "จ่ายบางส่วน รอสลิป": { iconKey: "alert-triangle", tone: "warning" },
  ต้องตรวจ: { iconKey: "alert-triangle", tone: "warning" },
  ต้องระวัง: { iconKey: "alert-circle", tone: "danger" },
  ปิดยอดแล้ว: { iconKey: "check-circle", tone: "success" },
  ยังไม่จ่าย: { iconKey: "alert-circle", tone: "danger" },
};

const STATUS_KEYWORD_MAP: Array<{ pattern: RegExp; iconKey: IconKey; tone: StatusTone }> = [
  { pattern: /(สำเร็จ|ตรวจสอบแล้ว|ต่ออายุแล้ว|ปิดงานแล้ว|บันทึกแล้ว)/i, iconKey: 'check-circle', tone: 'success' },
  { pattern: /(ไม่มีเบอร์|ยอดไม่ตรง|warning|pending)/i, iconKey: 'alert-triangle', tone: 'warning' },
  { pattern: /(ล้มเหลว|error|fail)/i, iconKey: 'alert-circle', tone: 'danger' },
  { pattern: /(ยกเลิก|cancel)/i, iconKey: 'x-circle', tone: 'inverse' },
]

export function isCancelledTransactionStatus(status: unknown): boolean {
  const text = getDisplayText(status)
  if (!text) return false
  if (CANCELLED_STATUS_PATTERN.test(text)) return true
  return getLeadingStatusEmoji(text) === '❌'
}

export function stripStatusEmoji(status: string) {
  return status.replace(/^[^\p{L}\p{N}]+/u, '').trim()
}

export function getLeadingStatusEmoji(status: string) {
  const trimmed = status.trim()
  const match = trimmed.match(/^[^\p{L}\p{N}\s]+/u)
  return match?.[0] ?? null
}

export function resolveStatusVisual(status: string) {
  status = getDisplayText(status)
  const bareLabel = stripStatusEmoji(status).trim()
  if (RECEIVABLE_STATUS_MAP[bareLabel]) {
    return {
      ...RECEIVABLE_STATUS_MAP[bareLabel],
      label: bareLabel,
    }
  }

  if (WORK_QUEUE_STATUS_MAP[bareLabel]) {
    return {
      ...WORK_QUEUE_STATUS_MAP[bareLabel],
      label: bareLabel,
    }
  }

  const leadingEmoji = getLeadingStatusEmoji(status)
  if (leadingEmoji && STATUS_EMOJI_ICON_MAP[leadingEmoji]) {
    const match = STATUS_EMOJI_ICON_MAP[leadingEmoji]
    return {
      ...match,
      label: stripStatusEmoji(status),
    }
  }

  for (const entry of STATUS_KEYWORD_MAP) {
    if (entry.pattern.test(status)) {
      return {
        iconKey: entry.iconKey,
        tone: entry.tone,
        label: stripStatusEmoji(status),
      }
    }
  }

  return {
    iconKey: 'help-circle' as const,
    tone: 'neutral' as const,
    label: stripStatusEmoji(status),
  }
}
